import { useCallback, useEffect, useMemo, useState } from "react";
import { LIVE_INTERVAL_MS, useVisibleInterval } from "../lib/polling";
import {
  Box, Stack, Typography, Alert, CircularProgress, IconButton, Tooltip,
  FormControlLabel, Switch, Paper, TextField, MenuItem, Tabs, Tab,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { fmtArDateTime } from "../services/monitoring";
import {
  getCollectors, fmtMoney, fmtBd,
  ACQ_CMF, CUENTA_CMF_COMPARTIDA, CUENTAS, matchCuenta, cuentaSufijo, type Cuenta,
  type Collectors,
} from "../services/monitoreoVivo";


const nf = new Intl.NumberFormat("es-AR");
const pf = new Intl.NumberFormat("es-AR", { style: "percent", minimumFractionDigits: 2, maximumFractionDigits: 2 });

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 180, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

export default function MonitoreoCollectors() {
  const [data, setData] = useState<Collectors | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [auto, setAuto] = useState(true);
  const [soloConSaldo, setSoloConSaldo] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [proc, setProc] = useState<string>("");
  const [cuenta, setCuenta] = useState<string>("todas");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await getCollectors();
      setData(d);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useVisibleInterval(load, LIVE_INTERVAL_MS, auto);

  // Solapas por procesador (AR#BIND / AR#CMFP / MX#STP / ...), en orden estable.
  const procs = useMemo(
    () => [...new Set((data?.rows ?? []).map((r) => r.processor))].sort(),
    [data],
  );
  const activeProc = procs.includes(proc) ? proc : (procs[0] ?? "");

  // Al cambiar de solapa, el filtro de cuenta vuelve a "todas": una cuenta de BIND
  // no tiene sentido en CMF y viceversa.
  useEffect(() => { setCuenta("todas"); }, [activeProc]);

  // Filtro por cuenta:
  //  - AR#CMFP: filtro curado (compartida / propias) alrededor de la cuenta CMF.
  //  - resto: dropdown de las cuentas distintas del procesador, SOLO si hay más de
  //    una (STP tiene una sola → no se ofrece). Filtra por cuenta exacta.
  const isCmf = activeProc === ACQ_CMF;
  const procAccounts = useMemo(
    () => [...new Set((data?.rows ?? []).filter((r) => r.processor === activeProc).map((r) => r.account || ""))].sort(),
    [data, activeProc],
  );
  const hayFiltroCuenta = isCmf || procAccounts.length > 1;
  const cuentaActiva = hayFiltroCuenta ? cuenta : "todas";
  const accountOptions = isCmf
    ? CUENTAS.map((o) => ({ value: o.value as string, label: o.label }))
    : [{ value: "todas", label: "Todas las cuentas" },
       ...procAccounts.map((a) => ({ value: a, label: a || "(cuenta por defecto)" }))];
  const matchAcc = (account: string) =>
    cuentaActiva === "todas" ? true
    : isCmf ? matchCuenta(cuentaActiva as Cuenta, account)
    : (account || "") === cuentaActiva;

  // Filas del universo: proc activo + cuenta elegida. La cuenta segmenta el
  // universo (tiles y TOTAL la siguen); buscador y "solo con saldo" son filtros
  // de vista y no tocan los totales.
  const procRows = useMemo(
    () => (data?.rows ?? []).filter((r) => r.processor === activeProc && matchAcc(r.account)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, activeProc, cuentaActiva, isCmf, procAccounts],
  );

  const rows = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    return procRows.filter((r) => {
      // La fila gris del informe (disponible y pendiente ambos 0) acá se oculta por default.
      if (soloConSaldo && r.disponible === 0 && r.pendiente === 0) return false;
      if (f && !r.collector.toLowerCase().includes(f)) return false;
      return true;
    });
  }, [procRows, soloConSaldo, filtro]);

  // Totales del proc activo (moneda única por proc).
  const procTotales = useMemo(() => {
    const t = { disponible: 0, pendiente: 0, bruto: 0, neto: 0 };
    for (const r of procRows) {
      t.disponible += r.disponible; t.pendiente += r.pendiente;
      t.bruto += r.bruto; t.neto += r.neto;
    }
    return t;
  }, [procRows]);
  const procCurrency = procRows[0]?.currency ?? "ARS";

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <Typography variant="h5">Estado de saldos</Typography>
          <Typography variant="body2" color="text.secondary">
            Disponible en cuenta + pendiente de liquidación por collector. Misma fuente que el informe "Estado de Collectors".
            Pendiente = cobros del día de negocio aún sin transferir.
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
        <TextField
          size="small" label="Buscar collector" value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        {hayFiltroCuenta && (
          <TextField
            size="small" select label={isCmf ? "Cuenta CMF" : "Cuenta"} value={cuentaActiva} sx={{ minWidth: 250 }}
            onChange={(e) => setCuenta(e.target.value)}
          >
            {accountOptions.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
        <FormControlLabel
          control={<Switch checked={soloConSaldo} onChange={(e) => setSoloConSaldo(e.target.checked)} />}
          label="Solo con saldo o pendiente"
        />
        <FormControlLabel
          control={<Switch checked={auto} onChange={(e) => setAuto(e.target.checked)} />}
          label={`Auto-refresh (${LIVE_INTERVAL_MS / 1000}s)`}
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        {loading && <CircularProgress size={18} />}
        {updatedAt && (
          <Typography variant="caption" color="text.secondary">
            Última actualización: {fmtArDateTime(updatedAt)}
          </Typography>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {data && data.errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No se pudo leer el estado de: {data.errors.map((e) => e.collector).join(", ")}
        </Alert>
      )}

      {data && (
        <Stack spacing={2}>
          <Tabs
            value={activeProc}
            onChange={(_e, v) => setProc(v)}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {procs.map((p) => (
              <Tab key={p} value={p}
                   label={`${p} (${nf.format(data.rows.filter((r) => r.processor === p).length)})`} />
            ))}
          </Tabs>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <StatTile label="Disponible" value={fmtMoney(procTotales.disponible, procCurrency)} />
            <StatTile label="Pendiente" value={fmtMoney(procTotales.pendiente, procCurrency)} color="#B45309" />
            <StatTile label="Neto" value={fmtMoney(procTotales.neto, procCurrency)} color="#166534" />
          </Stack>

          <Alert severity="info" variant="outlined" icon={false} sx={{ py: 0.5 }}>
            <Typography variant="body2">
              <strong>Fecha de negocio:</strong>{" "}
              {Object.entries(data.business_dates).map(([c, d]) => `${c} ${fmtBd(d)}`).join(" · ")}
              {"  ·  "}{nf.format(rows.length)} de {nf.format(procRows.length)} collectors
              {cuentaActiva !== "todas" && ` (${
                isCmf ? (cuentaActiva === "comp" ? `cuenta ${CUENTA_CMF_COMPARTIDA}` : "cuentas propias")
                      : (cuentaActiva ? `cuenta ${cuentaActiva}` : "cuenta por defecto")})`}
            </Typography>
          </Alert>

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Collector</TableCell>
                  <TableCell>Subp</TableCell>
                  <TableCell>Cuenta</TableCell>
                  <TableCell align="right">Disponible</TableCell>
                  <TableCell align="right">Pendiente</TableCell>
                  <TableCell align="right">Bruto</TableCell>
                  <TableCell align="right">% Com</TableCell>
                  <TableCell align="right">Neto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.collector}>
                    <TableCell sx={{ fontWeight: 600 }}>{r.collector}</TableCell>
                    <TableCell>{r.sub_processor}</TableCell>
                    <TableCell>{r.account}</TableCell>
                    <TableCell align="right">{fmtMoney(r.disponible, r.currency)}</TableCell>
                    <TableCell align="right" sx={{ color: r.pendiente > 0 ? "#B45309" : undefined }}>
                      {fmtMoney(r.pendiente, r.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.bruto, r.currency)}</TableCell>
                    <TableCell align="right">{pf.format(r.charge_rate)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtMoney(r.neto, r.currency)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                  <TableCell colSpan={3} sx={{ fontWeight: 700 }}>TOTAL {activeProc}{
                    cuentaActiva === "todas" ? ""
                    : isCmf ? cuentaSufijo(cuentaActiva as Cuenta)
                    : (cuentaActiva ? ` · cta ${cuentaActiva}` : " · cta por defecto")}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(procTotales.disponible, procCurrency)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(procTotales.pendiente, procCurrency)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(procTotales.bruto, procCurrency)}</TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(procTotales.neto, procCurrency)}</TableCell>
                </TableRow>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={8}>
                    <Typography variant="body2" color="text.secondary">Sin collectors para mostrar.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  );
}
